import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, MessageCircle } from "lucide-react";
import { CONTACT, DOCTORS, EYE, SURGERY, TIME_PREFS } from "@/data/site";
import { subscribeBookingDoctor } from "./bookingBus";
import { SectionHead, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL || process.env.PUBLIC_URL || ""}/api`;
const EMPTY = { name: "", phone: "", service: "", doctor: "", preferred_date: TIME_PREFS[0], message: "" };
const docLabel = (d) => `${d.nameHi} / ${d.nameEn}`;

const L = ({ id, children }) => <label htmlFor={id} className="mb-2 block text-sm font-semibold text-ink">{children}</label>;

const Success = ({ request, onReset }) => {
  const reference = request.id?.slice(-8).toUpperCase();
  const mailBody = [
    `Name: ${request.form.name}`,
    `Phone: ${request.form.phone}`,
    `Service: ${request.form.service}`,
    `Doctor: ${request.form.doctor || "Any available doctor"}`,
    `Preferred time: ${request.form.preferred_date || "Not specified"}`,
    `Message: ${request.form.message || "No message"}`,
    `Reference: ${reference}`,
  ].join("\n");
  const mailLink = `mailto:mail7flavours@gmail.com?subject=${encodeURIComponent("Medicity Hospital enquiry")}&body=${encodeURIComponent(mailBody)}`;
  return (
  <div data-testid="appointment-success" className="flex flex-col items-start rounded-[28px] bg-white p-6 sm:p-10">
    <CheckCircle2 className="h-12 w-12 text-green-600" />
    <h3 className="font-display mt-4 text-2xl text-ink sm:text-3xl">धन्यवाद! अनुरोध प्राप्त हुआ</h3>
    {request.emailSent
      ? <p className="mt-2 text-muted2">आपका अनुरोध हमारी टीम को ईमेल कर दिया गया है। हम जल्द ही आपको कॉल करेंगे।</p>
      : request.saved
        ? <p className="mt-2 text-muted2">आपका अनुरोध सुरक्षित है। ईमेल सेवा अभी सेट नहीं है; नीचे दिए लिंक से ईमेल भेजें या हमें कॉल करें।</p>
        : <p className="mt-2 text-muted2">वेबसाइट अभी अनुरोध सेव नहीं कर पाई। नीचे दिए लिंक से ईमेल भेजें या हमें कॉल करें।</p>}
    {reference && <p className="mt-2 text-muted2">Reference: <strong className="font-en text-ink" data-testid="appointment-ref">{reference}</strong></p>}
    {!request.emailSent && <a href={mailLink} className="btn btn-outline mt-5 text-maroon">ईमेल से अनुरोध भेजें</a>}
    <button onClick={onReset} data-testid="appointment-new-button" className="btn btn-outline mt-6 text-maroon">नया अपॉइंटमेंट</button>
  </div>
  );
};

export const AppointmentForm = ({ defaultDept }) => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const selectedDoctor = DOCTORS.find((doctor) => docLabel(doctor) === f.doctor);
  const serviceGroups = selectedDoctor
    ? [{ label: selectedDoctor.dept === "eye" ? "नेत्र सेवाएँ · Eye Care" : "सर्जरी · Surgery", services: selectedDoctor.dept === "eye" ? EYE : SURGERY }]
    : [];

  const setDoctor = (e) => {
    const doctor = e.target.value;
    setF((previous) => ({ ...previous, doctor, service: "" }));
  };

  useEffect(() => subscribeBookingDoctor((doctor) => setF((p) => ({ ...p, doctor }))), []);
  useEffect(() => {
    const d = DOCTORS.find((x) => x.dept === defaultDept);
    if (d) setF((p) => ({ ...p, doctor: p.doctor || docLabel(d) }));
  }, [defaultDept]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const submittedForm = { ...f };
    try {
      const { data } = await axios.post(`${API}/appointments`, submittedForm);
      setDone({ id: data.id, emailSent: data.email_sent === true, saved: true, form: submittedForm });
      setF(EMPTY);
      if (data.email_sent === true) toast.success("ईमेल सहित अनुरोध भेज दिया गया");
      else toast.warning("अनुरोध सुरक्षित है; ईमेल सेटिंग की आवश्यकता है");
    } catch (err) {
      const d = err.response?.data?.detail;
      const status = err.response?.status;
      if (status >= 500) {
        setDone({ id: null, emailSent: false, saved: false, form: submittedForm });
        toast.error("सर्वर अनुरोध पूरा नहीं कर पाया / The server could not process your enquiry. Please use the email link below.");
      } else if (status) {
        const detail = typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.map((item) => item.msg || item.message || "Invalid value").join(" · ")
            : "कृपया विवरण जाँचें / Please check your details";
        toast.error(detail);
      } else {
        setDone({ id: null, emailSent: false, saved: false, form: submittedForm });
        toast.warning("वेबसाइट से संपर्क नहीं हो पाया; ईमेल लिंक से भेजें");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="appointment" data-testid="appointment-section" className="dots-bg bg-maroon-tint py-20 md:py-28">
      <div className="container-x grid gap-12 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <SectionHead eyebrow="अपॉइंटमेंट · Book a Visit" title="एक मिनट में अपॉइंटमेंट बुक करें" sub="Fill the form or simply call / WhatsApp — our team will confirm your time." />
          <Reveal delay={0.1} className="mt-8 flex flex-col gap-3 sm:max-w-xs">
            <a href={`tel:${CONTACT.phoneDial}`} data-testid="appointment-call-button" className="btn btn-maroon"><Phone className="h-4 w-4" /> कॉल करें {CONTACT.phoneDisplay}</a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="appointment-whatsapp-button" className="btn btn-wa"><MessageCircle className="h-4 w-4" /> WhatsApp पर बुक करें</a>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-3">
          {done ? <Success request={done} onReset={() => setDone(null)} /> : (
            <form onSubmit={submit} data-testid="appointment-form" className="grid gap-5 rounded-[28px] bg-white p-6 shadow-xl shadow-maroon/5 sm:grid-cols-2 md:p-10">
              <div><L id="a-name">पूरा नाम *</L><input id="a-name" data-testid="appointment-name-input" required minLength={2} value={f.name} onChange={set("name")} className="field" placeholder="आपका नाम" /></div>
              <div><L id="a-phone">मोबाइल नंबर *</L><input id="a-phone" data-testid="appointment-phone-input" required type="tel" pattern="[0-9+ \-]{10,15}" value={f.phone} onChange={set("phone")} className="field" placeholder="10 अंकों का नंबर" /></div>
              <div>
                <L id="a-doctor">पहले डॉक्टर चुनें · Choose doctor first *</L>
                <select id="a-doctor" data-testid="appointment-doctor-select" required value={f.doctor} onChange={setDoctor} className="field">
                  <option value="">— डॉक्टर चुनें —</option>
                  {DOCTORS.map((d) => <option key={d.key} value={docLabel(d)}>{docLabel(d)}</option>)}
                </select>
              </div>
              <div>
                <L id="a-service">फिर सेवा चुनें · Then choose service *</L>
                <select id="a-service" data-testid="appointment-service-select" required disabled={!selectedDoctor} value={f.service} onChange={set("service")} className="field disabled:cursor-not-allowed disabled:bg-sand disabled:text-muted2">
                  <option value="">{selectedDoctor ? "— सेवा चुनें —" : "पहले डॉक्टर चुनें"}</option>
                  {serviceGroups.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.services.map((service) => <option key={service.slug} value={service.titleHi}>{service.titleHi}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <L id="a-time">पसंदीदा समय</L>
                <select id="a-time" data-testid="appointment-time-select" value={f.preferred_date} onChange={set("preferred_date")} className="field">
                  {TIME_PREFS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><L id="a-msg">संदेश (वैकल्पिक)</L><textarea id="a-msg" data-testid="appointment-message-input" rows={3} maxLength={500} value={f.message} onChange={set("message")} className="field resize-none" placeholder="अपनी समस्या संक्षेप में लिखें" /></div>
              <button data-testid="appointment-submit-button" disabled={loading} className="btn btn-maroon sm:col-span-2 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} अपॉइंटमेंट अनुरोध भेजें
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
